import { useCallback, useEffect, useState } from 'react';
import './SubmissionFormWizard.scss';
import {
    getGetV1SelfDeclarationPaginatedHistoryQueryKey,
    getGetV1SelfDeclarationPlannedSubmissionIdQueryKey,
    getGetV1SelfDeclarationQueryKey,
    useGetV1SelfDeclarationPlannedSubmissionId,
    useGetV1SelfDeclarationUnforeseenSubmissionId,
    usePostV1SelfDeclarationPlanned,
    usePostV1SelfDeclarationUnforeseen,
    usePutV1SelfDeclarationSubmissionId,
} from '../../api/generated/endpoint';
import useLocale from '../../locale/useLocale';
import { Formik, FormikProps } from 'formik';
import useGenericErrorToast from 'helpers/useGenericErrorToast';
import SubmissionTypeChoiceStepOne from 'pages/SubmissionFormWizard/SubmissionTypeChoiceStepOne/SubmissionTypeChoiceStepOne';
import * as Yup from 'yup';
import { Panel } from '@appkit4/react-components/panel';
import { Loading } from '@appkit4/react-components/loading';
import PlannedReasonStepTwo from './PlannedReasonStepTwo/PlannedReasonStepTwo';
import SubmissionWizardFooter from 'pages/SubmissionFormWizard/SubmissionWizardFooter/SubmissionWizardFooter';
import SubmissionStepThree from 'pages/SubmissionFormWizard/SubmissionStepThree/SubmissionStepThree';
import {
    PlannedDeclarationRequest,
    UnforeseenDeclarationRequest,
    PlannedDeclarationInformation,
    PlannedDeclarationResponse,
    UnforeseenDeclarationResponse,
    EditSelfDeclarationRequest,
    EditDeclaration,
} from 'api/generated/models';
import SubmissionStepFour from 'pages/SubmissionFormWizard/SubmissionStepFour/SubmissionStepFour';
import { AxiosError } from 'axios';
import formatDateToYYYYMMDD from 'helpers/formatDateToYYYYMMDD';
import { useLocation } from 'react-router-dom';
import { useAbsenceReasons } from 'contexts/AbsenceReasonsProvider/AbsenceReasonsProvider';
import useToast from 'helpers/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useUserDeclarations } from 'contexts/UserDeclarationsProvider/UserDeclarationsProvider';

export enum DeclarationTypes {
    Unforeseen = 'Unforeseen Circumstances',
    Planned = 'Planned Circumstances',
}

export type NewDeclarationFormValues = {
    declarationType: DeclarationTypes;
    reasonForAbsence?: string;
    dateRanges?: [
        {
            declarationId?: string;
            startDate: Date;
            endDate: Date;
        }
    ];
    unforeseenAbsences?: [
        {
            declarationId?: string;
            reasonForAbsence: string;
            startDate: Date;
        }
    ];
    declarationIdsToDelete?: string[];
    approverName?: string;
};

const SubmissionFormWizard = () => {
    const [step, setStep] = useState(0);
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();
    const [declarationType, setDeclarationType] = useState<
        DeclarationTypes | undefined
    >(undefined);
    const successToast = useToast();
    const queryClient = useQueryClient();

    const { absenseReasonsError } = useAbsenceReasons();
    const { error: userDeclarationsError } = useUserDeclarations();

    const location = useLocation();
    const { state } = location;
    const consecutiveUnforeseenAndSameReason =
        state?.consecutiveUnforeseenAndSameReason ?? false;

    const plannedReason =
        'Temporary business requirements (with partner or managing director agreement)';
    const submissionIdForEditFlow = location.pathname.split('/')[2];
    const declarationTypeFromHistory = location.pathname
        .split('/')[1]
        .replace('planned-circumstances', 'Planned')
        .replace('unforeseen-circumstances', 'Unforeseen');

    let previousPlannedSubmissionData: PlannedDeclarationResponse | undefined;
    let previousPlannedSubmissionDataIsPending = false;
    let previousPlannedSubmissionDataError:
        | AxiosError<string, any>
        | null
        | undefined = undefined;
    let previousUnforeseenSubmissionData:
        | UnforeseenDeclarationResponse
        | undefined;
    let previousUnforeseenSubmissionDataIsPending = false;
    let previousUnforeseenSubmissionDataError:
        | AxiosError<string, any>
        | null
        | undefined = undefined;

    if (declarationTypeFromHistory === 'Planned') {
        ({
            data: previousPlannedSubmissionData,
            isPending: previousPlannedSubmissionDataIsPending,
            error: previousPlannedSubmissionDataError,
        } = useGetV1SelfDeclarationPlannedSubmissionId(
            submissionIdForEditFlow,
            {
                query: {
                    enabled: !!submissionIdForEditFlow,
                },
            }
        ));
        previousPlannedSubmissionDataError =
            previousPlannedSubmissionDataError ?? undefined;
    } else if (declarationTypeFromHistory === 'Unforeseen') {
        ({
            data: previousUnforeseenSubmissionData,
            isPending: previousUnforeseenSubmissionDataIsPending,
            error: previousUnforeseenSubmissionDataError,
        } = useGetV1SelfDeclarationUnforeseenSubmissionId(
            submissionIdForEditFlow,
            {
                query: {
                    enabled: !!submissionIdForEditFlow,
                },
            }
        ));
        previousUnforeseenSubmissionDataError =
            previousUnforeseenSubmissionDataError ?? undefined;
    }

    useEffect(() => {
        if (submissionIdForEditFlow && declarationTypeFromHistory) {
            setDeclarationType(
                declarationTypeFromHistory === 'Planned'
                    ? DeclarationTypes.Planned
                    : declarationTypeFromHistory === 'Unforeseen'
                    ? DeclarationTypes.Unforeseen
                    : undefined
            );
            setStep(1);
        }
    }, [submissionIdForEditFlow, declarationTypeFromHistory]);

    const dateRangeSchemaPlanned = Yup.object().shape({
        declarationId: Yup.string().nullable(),
        startDate: Yup.date()
            .required('Start date is required')
            .typeError('Start date must be a valid date'),
        endDate: Yup.date()
            .required('End date is required')
            .typeError('End date must be a valid date')
            .min(Yup.ref('startDate'), 'End date cannot be before start date'),
    });
    const PlannedValidationSchema = Yup.object({
        declarationType: Yup.string().required('Type is required'),
        reasonForAbsence: Yup.string().required('Reason is required'),
        dateRanges: Yup.array()
            .of(dateRangeSchemaPlanned)
            .min(1, 'At least one date range is required')
            .required('Date ranges are required'),
        approverName: Yup.string()
            .trim()
            .max(120, 'Approver name must be 120 characters or fewer.')
            .matches(
                /^(?:[A-Za-zÀ-ÖØ-öø-ÿ0-9 +' -]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})$/,
                'Use letters, spaces, apostrophes, or hyphens only.'
            )
            .nullable()
            .when('reasonForAbsence', {
                is: (val: string | undefined) => val === plannedReason,
                then: (schema) =>
                    schema.required('Please enter the approver name.'),
                otherwise: (schema) =>
                    schema.notRequired().transform(() => null),
            }),
    });

    const unforeseenAbsenceSchema = Yup.object().shape({
        declarationId: Yup.string().nullable(),
        reasonForAbsence: Yup.string()
            .required('Reason for absence is required')
            .typeError('Reason for absence must be a string'),
        startDate: Yup.date()
            .required('Start date is required')
            .typeError('Start date must be a valid date'),
    });
    const UnforeseenValidationSchema = Yup.object({
        type: Yup.string().required('Type is required'),
        unforeseenAbsences: Yup.array()
            .of(unforeseenAbsenceSchema)
            .min(1, 'At least one date range is required')
            .required('Date ranges are required'),
    });

    const steps = useCallback(
        () =>
            [
                {
                    component: (
                        <SubmissionTypeChoiceStepOne
                            setDeclarationType={setDeclarationType}
                        />
                    ),
                },
                ...(declarationType === DeclarationTypes.Planned
                    ? [
                          {
                              component: (
                                  <PlannedReasonStepTwo
                                      submissionIdForEditFlow={
                                          submissionIdForEditFlow
                                      }
                                  />
                              ),
                          },
                          {
                              component: (
                                  <SubmissionStepThree
                                      submissionIdForEditFlow={
                                          submissionIdForEditFlow
                                      }
                                      previousPlannedSubmissionData={
                                          previousPlannedSubmissionData
                                      }
                                      previousPlannedSubmissionDataIsPending={
                                          previousPlannedSubmissionDataIsPending
                                      }
                                      previousUnforeseenSubmissionData={
                                          previousUnforeseenSubmissionData
                                      }
                                      previousUnforeseenSubmissionDataIsPending={
                                          previousUnforeseenSubmissionDataIsPending
                                      }
                                      consecutiveUnforeseenAndSameReason={
                                          consecutiveUnforeseenAndSameReason
                                      }
                                  />
                              ),
                          },
                      ]
                    : [
                          {
                              component: (
                                  <SubmissionStepThree
                                      submissionIdForEditFlow={
                                          submissionIdForEditFlow
                                      }
                                      previousPlannedSubmissionData={
                                          previousPlannedSubmissionData
                                      }
                                      previousPlannedSubmissionDataIsPending={
                                          previousPlannedSubmissionDataIsPending
                                      }
                                      previousUnforeseenSubmissionData={
                                          previousUnforeseenSubmissionData
                                      }
                                      previousUnforeseenSubmissionDataIsPending={
                                          previousUnforeseenSubmissionDataIsPending
                                      }
                                      consecutiveUnforeseenAndSameReason={
                                          consecutiveUnforeseenAndSameReason
                                      }
                                  />
                              ),
                          },
                      ]),
                {
                    component: (
                        <SubmissionStepFour
                            submissionIdForEditFlow={submissionIdForEditFlow}
                        />
                    ),
                },
            ].filter(
                (step): step is { component: JSX.Element } => step !== undefined
            ),
        [
            declarationType,
            previousPlannedSubmissionData,
            previousPlannedSubmissionDataIsPending,
            previousUnforeseenSubmissionData,
            previousUnforeseenSubmissionDataIsPending,
            submissionIdForEditFlow,
        ]
    );

    const {
        mutateAsync: submitPlannedDeclaration,
        isPending: submitPlannedDeclarationIsPending,
        error: submitPlannedDeclarationError,
    } = usePostV1SelfDeclarationPlanned({
        mutation: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationPaginatedHistoryQueryKey(),
                });
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationQueryKey(),
                });
            },
        },
    });

    const {
        mutateAsync: submitUnforeseenDeclaration,
        isPending: submitUnforeseenDeclarationIsPending,
        error: submitUnforeseenDeclarationError,
    } = usePostV1SelfDeclarationUnforeseen({
        mutation: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationPaginatedHistoryQueryKey(),
                });
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationQueryKey(),
                });
            },
        },
    });

    const {
        mutateAsync: editDeclaration,
        isPending: editDeclarationIsPending,
        error: editDeclarationError,
    } = usePutV1SelfDeclarationSubmissionId({
        mutation: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey:
                        getGetV1SelfDeclarationPlannedSubmissionIdQueryKey(
                            submissionIdForEditFlow
                        ),
                });
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationPaginatedHistoryQueryKey(),
                });
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationQueryKey(),
                });
            },
        },
    });

    useEffect(() => {
        const error =
            absenseReasonsError ||
            previousPlannedSubmissionDataError ||
            previousUnforeseenSubmissionDataError ||
            editDeclarationError;

        if (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        }
    }, [
        absenseReasonsError,
        previousPlannedSubmissionDataError,
        previousUnforeseenSubmissionDataError,
        editDeclarationError,
        showGenericErrorToast,
    ]);

    useEffect(() => {
        if (userDeclarationsError) {
            showGenericErrorToast(l('_errorFetchingSubmissionData'), 600000);
        }
    }, [userDeclarationsError, showGenericErrorToast]);

    const handleLeftButtonClick = () => {
        setStep((prevStep) => prevStep - 1);
    };

    const handleSubmit = async (values: any) => {
        if (declarationType === DeclarationTypes.Planned) {
            const isSpecialReason = values.reasonForAbsence === plannedReason;
            const payload: PlannedDeclarationRequest = {
                declarationType: values.declarationType,
                reasonForAbsence: values.reasonForAbsence,
                dateRanges: values.dateRanges.map(
                    ({
                        startDate,
                        endDate,
                    }: {
                        startDate: Date;
                        endDate: Date;
                    }) => ({
                        startDate: formatDateToYYYYMMDD(startDate),
                        endDate: formatDateToYYYYMMDD(endDate),
                    })
                ),
                approverName: isSpecialReason ? values.approverName : null,
            };
            if (submissionIdForEditFlow) {
                const declarations: EditDeclaration[] = values.dateRanges.map(
                    (dateRange: any) => ({
                        declarationId: dateRange.declarationId || undefined,
                        declarationType: values.declarationType,
                        startDate: formatDateToYYYYMMDD(dateRange.startDate),
                        endDate: formatDateToYYYYMMDD(dateRange.endDate),
                        reasonForAbsence: values.reasonForAbsence,
                        approverName: isSpecialReason
                            ? values.approverName
                            : null,
                    })
                );
                const editPayload: EditSelfDeclarationRequest = {
                    declarations: declarations,
                    declarationIdsToDelete: values.declarationIdsToDelete || [],
                };
                await editDeclaration({
                    submissionId: submissionIdForEditFlow,
                    data: editPayload,
                });
            } else {
                await submitPlannedDeclaration({ data: payload });
            }
        } else if (declarationType === DeclarationTypes.Unforeseen) {
            const payload: UnforeseenDeclarationRequest = {
                declarationType: values.declarationType,
                unforeseenAbsences: values.unforeseenAbsences.map(
                    (unforeseenAbsence: {
                        reasonForAbsence: string;
                        startDate: Date;
                    }) => ({
                        startDate: formatDateToYYYYMMDD(
                            unforeseenAbsence.startDate
                        ),
                        reasonForAbsence: unforeseenAbsence.reasonForAbsence,
                    })
                ),
            };
            if (submissionIdForEditFlow) {
                const declarations: EditDeclaration[] =
                    values.unforeseenAbsences.map(
                        (unforeseenAbsence: EditDeclaration) => ({
                            declarationId:
                                unforeseenAbsence.declarationId || null,
                            declarationType: values.declarationType,
                            startDate:
                                unforeseenAbsence.startDate === undefined
                                    ? ''
                                    : formatDateToYYYYMMDD(
                                          new Date(unforeseenAbsence?.startDate)
                                      ),
                            endDate: null,
                            reasonForAbsence:
                                unforeseenAbsence.reasonForAbsence,
                        })
                    );
                const editPayload: EditSelfDeclarationRequest = {
                    declarations: declarations,
                    declarationIdsToDelete: values.declarationIdsToDelete || [],
                };
                await editDeclaration({
                    submissionId: submissionIdForEditFlow,
                    data: editPayload,
                });
            } else {
                await submitUnforeseenDeclaration({ data: payload });
            }
        }
    };

    const handleRightButtonClick = useCallback(
        async (
            formikProps: FormikProps<NewDeclarationFormValues>
        ): Promise<void> => {
            const shouldSubmitUnforeseen =
                step === 1 && declarationType === DeclarationTypes.Unforeseen;

            const shouldSubmitPlanned =
                step === 2 && declarationType === DeclarationTypes.Planned;

            try {
                if (shouldSubmitUnforeseen || shouldSubmitPlanned) {
                    await handleSubmit(formikProps.values);
                    const isPending = shouldSubmitUnforeseen
                        ? submitUnforeseenDeclarationIsPending
                        : submitPlannedDeclarationIsPending;
                    const error = shouldSubmitUnforeseen
                        ? submitUnforeseenDeclarationError
                        : submitPlannedDeclarationError;

                    if (!isPending && !error) {
                        successToast(
                            l('_success'),
                            l('_successfulSubmission'),
                            'success'
                        );
                        setStep((prevStep) => prevStep + 1);
                        return;
                    }
                }
            } catch (error) {
                showGenericErrorToast(error as AxiosError<string, any>);
                return;
            }

            if (!shouldSubmitUnforeseen && !shouldSubmitPlanned) {
                setStep((prevStep) => prevStep + 1);
            }
        },
        [
            declarationType,
            l,
            setStep,
            showGenericErrorToast,
            submitPlannedDeclarationError,
            submitPlannedDeclarationIsPending,
            submitUnforeseenDeclarationError,
            submitUnforeseenDeclarationIsPending,
            successToast,
            step,
            handleSubmit,
        ]
    );

    const { component: CurrentStep } = steps()[step] || {};

    const [submissionIsPending, setSubmissionIsPending] =
        useState<boolean>(false);

    useEffect(() => {
        setSubmissionIsPending(
            submitPlannedDeclarationIsPending ||
                submitUnforeseenDeclarationIsPending ||
                editDeclarationIsPending
        );
    }, [
        submitPlannedDeclarationIsPending,
        submitUnforeseenDeclarationIsPending,
        editDeclarationIsPending,
    ]);

    const getValidationSchema = (declarationType?: DeclarationTypes) =>
        declarationType === DeclarationTypes.Planned
            ? PlannedValidationSchema
            : declarationType === DeclarationTypes.Unforeseen
            ? UnforeseenValidationSchema
            : Yup.object();

    const preparePlannedDateRanges = useCallback(() => {
        const declarationInfo: PlannedDeclarationInformation[] | null =
            previousPlannedSubmissionData?.declarationInformation ?? null;
        if (declarationInfo) {
            return declarationInfo.map((info) => ({
                declarationId: info.declarationId,
                startDate: new Date(info.dateRange.startDate),
                endDate: new Date(info.dateRange.endDate),
            }));
        }
        return null;
    }, [previousPlannedSubmissionData]);

    const preparePreviousUnforeseenAbsences = useCallback(() => {
        const unforeseenAbsenceInfo =
            previousUnforeseenSubmissionData?.unforeseenAbsences;
        if (unforeseenAbsenceInfo) {
            return unforeseenAbsenceInfo.map((info) => ({
                declarationId: info.declarationId,
                startDate: new Date(info.startDate),
                reasonForAbsence: info.reasonForAbsence,
            }));
        }
    }, [previousUnforeseenSubmissionData]);

    return (
        <>
            {previousPlannedSubmissionDataIsPending ||
            previousUnforeseenSubmissionDataIsPending ||
            submissionIsPending ? (
                <Loading
                    loadingType="circular"
                    indeterminate
                    compact={false}
                    className="mt-2"
                />
            ) : (
                <Formik
                    initialValues={{
                        ...(previousPlannedSubmissionData && {
                            declarationType: DeclarationTypes.Planned,
                            reasonForAbsence:
                                previousPlannedSubmissionData.reasonForAbsence,
                            dateRanges: preparePlannedDateRanges(),
                            approverName:
                                previousPlannedSubmissionData.reasonForAbsence ===
                                plannedReason
                                    ? previousPlannedSubmissionData.approverName ??
                                      ''
                                    : null,
                        }),
                        ...(previousUnforeseenSubmissionData && {
                            declarationType: DeclarationTypes.Unforeseen,
                            unforeseenAbsences:
                                preparePreviousUnforeseenAbsences(),
                        }),
                        declarationIdsToDelete: [],
                    }}
                    enableReinitialize={true}
                    validationSchema={getValidationSchema(declarationType)}
                    onSubmit={() => {
                        console.log('Submission complete');
                    }}
                >
                    {() => (
                        <form>
                            <Panel className={'mt-4'}>
                                <div className="pt-2">
                                    <h1>
                                        {step === steps().length - 1
                                            ? l('_thankYou')
                                            : submissionIdForEditFlow
                                            ? l('_editPreviousSubmission')
                                            : l('_applicationName')}
                                    </h1>
                                </div>

                                {CurrentStep}

                                <SubmissionWizardFooter
                                    step={step}
                                    stepsLength={steps().length}
                                    handleLeftButtonClick={
                                        handleLeftButtonClick
                                    }
                                    handleRightButtonClick={
                                        handleRightButtonClick
                                    }
                                    submissionIsPending={submissionIsPending}
                                    submissionIdForEditFlow={
                                        submissionIdForEditFlow
                                    }
                                />
                            </Panel>
                        </form>
                    )}
                </Formik>
            )}
        </>
    );
};
export default SubmissionFormWizard;
